import React from "react";
import { produce } from "immer";
import styles from "./dialog.module.css";
import modal from "src/components/ui-components/Modal/ModalContent.module.css";
import { Modal } from "src/components/ui-components/Modal/Modal";
import { ParameterEnum } from "src/components/ParameterEnum/ParameterEnum";
import { InputField } from "src/components/ui-components/Input/InputField";
import { SelectBox } from "src/components/ui-components/Input/SelectBox";
import { InputCheckbox } from "src/components/ui-components/Input/InputCheckbox";
import { FormField } from "src/components/ui-components/Input/FormField";
import { TabList } from "src/components/ui-components/Tabs/TabList";
import { TabContent } from "src/components/ui-components/Tabs/TabContent";
import { Tab } from "src/components/ui-components/Tabs/Tab";
import { Button } from "src/components/ui-components/Button/Button";
import { IconButton } from "src/components/ui-components/Button/IconButton";
import { AnchorButtonTextOnly } from "src/components/ui-components/ButtonTextOnly/AnchorButtonTextOnly";
import { Icon } from "src/components/icon/icon";
import { IconCross } from "src/components/ui-components/Icons/IconCross";
import { IconArrowSquareOut } from "src/components/ui-components/Icons/IconArrowSquareOut";
import { Datepicker } from "src/components/datepicker/datepicker";
import { FieldLabel } from "src/components/input/fields";
import { Fields } from "src/components/input/fields";
import { InputTextArea } from "src/components/input/input";
import { Parameter } from "src/utils/parameters/types";
import { formatEnumOptions } from "src/utils/parameters/parameters";
import { logger } from "src/utils/logger/browser";
import { parseEnumOptions } from "src/utils/parameters/parameters";
import { createParameterTag } from "src/utils/parameters/parameters";
import { useParameterEnumOptions } from "src/hooks/useParameterEnumOptions";
import { useIsFeatureEnabled } from "src/hooks/useIsFeatureEnabled";
import { isNonZeroNumber } from "src/utils/types/types";

interface Props {
  parameter: Parameter;
  parameters: Parameter[];
  onRemove?: (parameter: Parameter) => void;
  onReplace?: (parameter: Parameter) => void;
}

export const ParameterDialogButton: React.FC<Props> = (props) => {
  return (
    <Modal
      size="M"
      label="Edit"
      content={({ close }) => <ParameterDialog {...props} close={close} />}
      trigger={({ onClick }) => (
        <IconButton size="S" theme="ghost" onClick={onClick}>
          <Icon icon="gear" />
        </IconButton>
      )}
    />
  );
};

const ParameterDialog: React.FC<
  Props & {
    close: () => void;
  }
> = (props) => {
  const [draft, setDraft] = React.useState(props.parameter);
  const [loading] = React.useState(false);

  const tag = createParameterTag(draft);

  const enumOptions = useParameterEnumOptions(draft);

  const [enumInput, setEnumInput] = React.useState(() => {
    return formatEnumOptions(props.parameter.enumOptions);
  });

  const parameterNameInputError = (() => {
    if (!draft.key) {
      return "Parameter name required";
    }

    const duplicateKey =
      props.parameter.key !== draft.key &&
      props.parameters.some((p) => p.key === draft.key);

    if (duplicateKey) {
      return "Parameter name taken";
    }
  })();

  const parameterValInputError = useParameterInputError(draft);

  const queryIdInputError =
    draft.enumFromResults && draft.enumFromResults.queryId === UNSET_QUERY_ID
      ? "Provide the id of a query to get list options from"
      : undefined;

  const columnNameInputError = (() => {
    if (!draft.enumFromResults || queryIdInputError !== undefined) {
      return;
    }
    if (draft.enumFromResults.columnName === "") {
      return "Provide the name of the column that contains the list options";
    }
    if (!enumOptions) {
      return `Column "${draft.enumFromResults.columnName}" was not found on query ${draft.enumFromResults.queryId}`;
    }
  })();

  function hasError(): boolean {
    if (
      parameterNameInputError !== undefined ||
      queryIdInputError !== undefined ||
      columnNameInputError !== undefined ||
      parameterValInputError !== undefined
    ) {
      return true;
    }
    return false;
  }

  const formValuesRef = React.useRef({
    enumFromResults: draft.enumFromResults ?? {
      queryId: UNSET_QUERY_ID,
      columnName: "",
    },
  });
  (function keepRefUpdated() {
    const { current } = formValuesRef;

    current.enumFromResults = draft.enumFromResults ?? current.enumFromResults;
  })();

  const newParamFeaturesEnabled = useIsFeatureEnabled("new-parameter-features");

  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (!hasError()) {
      props.onReplace?.(draft);
      props.close();
    }
  };

  const onKey = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.value;

    setDraft({ ...draft, key });
  };

  const onDescription = (event: React.ChangeEvent<HTMLInputElement>) => {
    const description = event.target.value;

    setDraft({ ...draft, description });
  };

  const onType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedType = event.target.value as Parameter["type"];

    if (selectedType === "enum") {
      setDraft({
        ...draft,
        type: "enum",
        value: "",
        values: undefined,
        isMultiselect: false,
        enumOptions: parseEnumOptions(enumInput),
      });
    } else {
      setDraft({
        ...draft,
        type: selectedType,
        value: "",
        values: undefined,
        isMultiselect: undefined,
        enumOptions: undefined,
      });
    }
  };

  function onFreeformAllowed(event: React.ChangeEvent<HTMLInputElement>) {
    if (draft.type !== "enum") {
      throw Error(
        `Cannot set "isFreeformAllowed" for parameter of type "${draft.type}"`
      );
    }

    setDraft({
      ...draft,
      isFreeformAllowed: event.target.checked,
    });
  }

  function onMultiselect(event: React.ChangeEvent<HTMLInputElement>) {
    if (draft.type !== "enum") {
      throw Error(
        `Cannot set "isMultiselect" for parameter of type "${draft.type}"`
      );
    }

    if (event.target.checked) {
      setDraft({
        ...draft,
        isMultiselect: true,
        value: undefined,
        values: [draft.value].filter(
          (value): value is string => value !== undefined && value !== ""
        ),
      });
    } else {
      setDraft({
        ...draft,
        isMultiselect: false,
        value: draft.values?.[0] ?? "",
        values: undefined,
      });
    }
  }

  function onEnumFromResultsToggle(enabled: boolean) {
    if (draft.type !== "enum") {
      throw Error(
        `Cannot set "enumFromResults" for parameter of type "${draft.type}"`
      );
    }

    if (enabled) {
      const { enumFromResults } = formValuesRef.current;

      setDraft({
        ...draft,
        enumFromResults,
        enumOptions: undefined,
      });
    } else {
      setDraft({
        ...draft,
        enumFromResults: undefined,
        enumOptions: parseEnumOptions(enumInput),
      });
    }
  }

  function onEnumFromResultsQueryId(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    if (draft.type !== "enum") {
      throw Error(
        `Cannot set "enumFromResults" for parameter of type "${draft.type}"`
      );
    }

    const queryId =
      event.target.value === "" ? UNSET_QUERY_ID : parseInt(event.target.value);

    if (Number.isNaN(queryId)) {
      return;
    }

    setDraft(
      produce((draft: Parameter) => {
        draft.enumFromResults!.queryId = queryId;
      })
    );
  }

  function onEnumFromResultsColumnName(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    if (draft.type !== "enum") {
      throw Error(
        `Cannot set "enumFromResults" for parameter of type "${draft.type}"`
      );
    }

    const columnName = event.target.value;

    setDraft(
      produce((draft: Parameter): Parameter => {
        draft.enumFromResults!.columnName = columnName;
        return draft;
      })
    );
  }

  const onEnumOptions = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = event.target.value;
    const enumOptions = parseEnumOptions(input);
    const value = enumOptions[0] ?? "";
    setEnumInput(input);
    setDraft({ ...draft, enumOptions, value });
  };

  return (
    <div className={modal.body}>
      <div className={modal.header}>
        <div className={modal.titleRow}>
          <h2>Edit parameter</h2>

          <IconButton size="XS" theme="ghost" onClick={props.close}>
            <IconCross />
          </IconButton>
        </div>
      </div>

      <form className={modal.subBody} onSubmit={onSubmit}>
        <Fields>
          <InputField
            size="M"
            style="outlined"
            label="Parameter name"
            placeholder=""
            required
            value={draft.key}
            onChange={onKey}
            error={parameterNameInputError}
            hint={
              parameterNameInputError === undefined &&
              draft.key &&
              `Wrap your parameter in single quotes to use as a value '${tag}' in SQL.`
            }
          />

          {newParamFeaturesEnabled && (
            <InputField
              size="M"
              style="outlined"
              label="Parameter description"
              placeholder=""
              value={draft.description ?? ""}
              onChange={onDescription}
            />
          )}

          <SelectBox
            size="M"
            type="outlined"
            label="Parameter type"
            name="Parameter type"
            placeholder=""
            value={draft.type ?? "text"}
            onChange={onType}
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="datetime">Date</option>
            <option value="enum">List</option>
          </SelectBox>

          <ParameterInput parameter={draft} onChange={setDraft} />

          {newParamFeaturesEnabled && draft.type === "enum" && (
            <InputCheckbox
              label="Freeform"
              checked={draft.isFreeformAllowed ?? false}
              onChange={onFreeformAllowed}
            >
              Allow selecting any value, not just specified options
            </InputCheckbox>
          )}

          {newParamFeaturesEnabled && draft.type === "enum" && (
            <InputCheckbox
              label="Multiselect list"
              checked={draft.isMultiselect ?? false}
              onChange={onMultiselect}
            >
              Allow selecting multiple options from the list
            </InputCheckbox>
          )}

          {newParamFeaturesEnabled && draft.type === "enum" && (
            <TabList size="M" fillWidth marginBottom={false}>
              <Tab
                active={draft.enumFromResults === undefined}
                onClick={() => onEnumFromResultsToggle(false)}
              >
                Manually specify list options
              </Tab>
              <Tab
                active={typeof draft.enumFromResults === "object"}
                onClick={() => onEnumFromResultsToggle(true)}
              >
                Get list options from a query
              </Tab>
            </TabList>
          )}
          <TabContent
            active={
              draft.type === "enum" &&
              Boolean(draft.enumFromResults ?? draft.enumOptions)
            }
          >
            {draft.enumFromResults ? (
              <>
                <InputField
                  size="M"
                  style="outlined"
                  label="Query id"
                  required
                  placeholder=""
                  hint={
                    isNonZeroNumber(draft.enumFromResults.queryId) ? (
                      <AnchorButtonTextOnly
                        size="M"
                        theme="tertiary"
                        insideText
                        href={`/queries/${draft.enumFromResults.queryId}`}
                        target="_blank"
                      >
                        Open query
                        <IconArrowSquareOut />
                      </AnchorButtonTextOnly>
                    ) : undefined
                  }
                  value={
                    draft.enumFromResults.queryId === UNSET_QUERY_ID
                      ? ""
                      : String(draft.enumFromResults.queryId)
                  }
                  onChange={onEnumFromResultsQueryId}
                  error={queryIdInputError}
                />
                <InputField
                  size="M"
                  style="outlined"
                  label="Column name"
                  required
                  placeholder=""
                  value={draft.enumFromResults.columnName}
                  onChange={onEnumFromResultsColumnName}
                  error={columnNameInputError}
                />
              </>
            ) : (
              <FieldLabel
                label="List options"
                caption="Separate options with newlines."
              >
                <InputTextArea value={enumInput} onChange={onEnumOptions} />
              </FieldLabel>
            )}
          </TabContent>
        </Fields>

        <div className={modal.buttons}>
          {props.onRemove && (
            <>
              <Button
                size="M"
                theme="danger"
                type="button"
                onClick={props.onRemove.bind(null, props.parameter)}
                loading={loading}
              >
                Remove
              </Button>

              <hr className={modal.gapFillMiddle} />
            </>
          )}

          <Button
            size="M"
            theme="tertiary"
            type="button"
            loading={loading}
            onClick={props.close}
          >
            Cancel
          </Button>

          <Button
            size="M"
            theme="primary"
            type="submit"
            loading={loading}
            disabled={hasError()}
          >
            Save
          </Button>
        </div>
      </form>
    </div>
  );
};

const ParameterInput: React.FC<{
  parameter: Parameter;
  onChange: (parameter: Parameter) => void;
}> = (props) => {
  const { value = "", values = [] } = props.parameter;
  const parameterValInputError = useParameterInputError(props.parameter);

  const onString = (value: string) => {
    props.onChange({
      ...props.parameter,
      value,
    });
  };

  if (props.parameter.type === "text") {
    return (
      <InputField
        size="M"
        style="outlined"
        label="Default text"
        placeholder=""
        required
        value={value}
        onChange={(event) => onString(event.target.value)}
      />
    );
  }

  if (props.parameter.type === "number") {
    return (
      <InputField
        size="M"
        style="outlined"
        label="Default number"
        placeholder=""
        required
        type="number"
        value={value}
        onChange={(event) => onString(event.target.value)}
      />
    );
  }

  if (props.parameter.type === "datetime") {
    return (
      <FormField
        id={props.parameter.key}
        size="M"
        style="outlined"
        label={props.parameter.key}
        renderInput={(props) => (
          <Datepicker
            kind="dialog"
            onChange={onString}
            value={value}
            required={true}
            wrapperClassNameOverride={styles.dateParameterField}
            customInput={<input {...props} value={value} type="string" />}
          />
        )}
      />
    );
  }

  if (props.parameter.type === "enum") {
    const { isMultiselect = false } = props.parameter;

    if (!isMultiselect) {
      return (
        <ParameterEnum
          parameter={props.parameter}
          parameterValues={value === "" ? [] : [value]}
          onChange={props.onChange}
          renderFormField={({ renderButton, disabled }) => (
            <FormField
              size="M"
              style="outlined"
              label="Default selection"
              disabled={disabled}
              renderInput={renderButton}
              error={parameterValInputError}
            />
          )}
        />
      );
    } else {
      return (
        <ParameterEnum
          parameter={props.parameter}
          parameterValues={values}
          onChange={props.onChange}
          renderFormField={({ renderButton, disabled }) => (
            <FormField
              size="M"
              style="outlined"
              label="Default selection"
              disabled={disabled}
              renderInput={renderButton}
              error={parameterValInputError}
            />
          )}
        />
      );
    }
  }

  logger.warn("unsupported parameter type", props.parameter["type"]);
  return null;
};

function useParameterInputError(parameter: Parameter): string | undefined {
  const { value = "", values = [] } = parameter;
  const enumOptions = useParameterEnumOptions(parameter);

  switch (parameter.type) {
    case "enum": {
      const { isMultiselect = false } = parameter;

      if (!isMultiselect) {
        if (value === "") {
          return "Select a default value";
        }
        if (enumOptions && !enumOptions.includes(value)) {
          return "Invalid value";
        }
      } else {
        if (values.length === 0) {
          return "Select a default value";
        }
        if (
          enumOptions &&
          values.some((value) => !enumOptions.includes(value))
        ) {
          return "Invalid value";
        }
      }
    }
  }
}

const UNSET_QUERY_ID = 0;
